import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AuthenticatedUser } from "../lib/api";
import { logout } from "../lib/api";
import { continueUrl } from "../lib/redirect";

/**
 * What somebody who already has a session sees when they land here: usually
 * because they typed the address, occasionally because a project sent them and
 * their session turned out to be fine.
 *
 * It shows who they are and offers the two things they can want. Signing them
 * out automatically would be wrong, and redirecting them onward without a click
 * would make this page impossible to visit deliberately.
 */
export function SignedIn({
  user,
  redirectUri,
}: {
  user: AuthenticatedUser;
  redirectUri: string | null;
}) {
  const queryClient = useQueryClient();

  const signOut = useMutation({
    mutationFn: logout,

    // Refetch rather than write the answer locally. The API has just cleared
    // the cookies, so the next query returns the truth, and a local guess would
    // be wrong in the one case that matters: a sign-out that failed server-side.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["session"] }),
  });

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-adriatic-deep/5 px-4 py-3 text-center">
        <p className="text-sm text-adriatic-mid">Signed in as</p>
        <p className="font-medium break-all text-adriatic-deep">{user.name ?? user.email}</p>
        {user.name && <p className="text-sm break-all text-adriatic-mid">{user.email}</p>}
      </div>

      {/* An anchor, not a fetch. This is a navigation to an endpoint that
          answers with a redirect, and letting the browser follow it is what
          keeps the destination decision on the server. */}
      <a
        href={continueUrl(redirectUri)}
        className="block w-full rounded-full bg-coral px-4 py-2.5 text-center font-semibold text-white transition-colors hover:bg-coral-light hover:text-adriatic-deep focus-visible:ring-2 focus-visible:ring-acqua/50 focus-visible:outline-none"
      >
        Continue
      </a>

      <button
        type="button"
        onClick={() => signOut.mutate()}
        disabled={signOut.isPending}
        className="w-full rounded-full border border-adriatic/25 px-4 py-2.5 font-medium text-adriatic-deep transition-colors hover:bg-adriatic-deep/5 focus-visible:ring-2 focus-visible:ring-acqua/50 focus-visible:outline-none disabled:opacity-60"
      >
        {signOut.isPending ? "Signing out..." : "Sign out"}
      </button>

      {signOut.isError && (
        <p role="alert" className="text-sm text-coral-ink">
          Could not sign out. Try again.
        </p>
      )}

      <p className="text-center text-xs text-adriatic-mid">
        Signing out ends this session for every AI Labs project.
      </p>
    </div>
  );
}
